import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Package, ShoppingCart, DollarSign, RefreshCcw, TrendingUp, User,
} from "lucide-react";
import { useScenario } from "../../store";
import { SELLERS } from "../../mocks/seed";
import { NotificationsPanel } from "../../design-system/components/NotificationsPanel";
import { LegacyAppShell } from "./LegacyAppShell";
import { apiMode } from "@/services";
import { api } from "@/services/http/client";

const RAIL_ITEMS = [
  { id: "dashboard", icon: LayoutDashboard, label: "Dashboard", path: "/seller" },
  { id: "products",  icon: Package,         label: "Products",  path: "/seller/products" },
  { id: "orders",    icon: ShoppingCart,    label: "Orders",    path: "/seller/orders" },
  { id: "returns",   icon: RefreshCcw,      label: "Returns",   path: "/seller/returns" },
  { id: "earnings",  icon: TrendingUp,      label: "Earnings",  path: "/seller/earnings" },
  { id: "payouts",   icon: DollarSign,      label: "Payouts",   path: "/seller/payouts" },
  { id: "profile",   icon: User,            label: "Profile",   path: "/seller/profile" },
];

const TABS: Record<string, { label: string; path: string; end?: boolean }[]> = {
  dashboard: [{ label: "Seller Dashboard", path: "/seller", end: true }],
  products:  [{ label: "Products & Offers", path: "/seller/products" }],
  orders:    [{ label: "All Orders", path: "/seller/orders" }],
  returns:   [{ label: "Return Requests", path: "/seller/returns" }],
  earnings:  [{ label: "Earnings", path: "/seller/earnings" }],
  payouts:   [{ label: "Payouts", path: "/seller/payouts" }],
  profile:   [{ label: "Seller Profile", path: "/seller/profile" }],
};

function getActiveSection(pathname: string) {
  if (pathname === "/seller" || pathname === "/seller/") return "dashboard";
  if (pathname.startsWith("/seller/product")) return "products";
  if (pathname.startsWith("/seller/order")) return "orders";
  if (pathname.startsWith("/seller/return")) return "returns";
  if (pathname.startsWith("/seller/earning")) return "earnings";
  if (pathname.startsWith("/seller/payout")) return "payouts";
  if (pathname.startsWith("/seller/profile")) return "profile";
  return "dashboard";
}

export function SellerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const scenario = useScenario();
  const activeSection = getActiveSection(location.pathname);
  const seller = SELLERS.find((s) => s.id === scenario.sellerId) ?? SELLERS[0];
  const [live, setLive] = useState<{ name: string; subtitle: string } | null>(null);
  useEffect(() => {
    if (!apiMode) return;
    api<{ seller: { name: string }; user: { name: string } }>("/api/seller/me")
      .then((m) => setLive({ name: m.user.name, subtitle: m.seller.name }))
      .catch(() => setLive({ name: "Seller", subtitle: "" }));
  }, []);
  const identity = apiMode
    ? (live ?? { name: "…", subtitle: "" })
    : { name: scenario.userName, subtitle: seller?.name ?? "" };

  return (
    <LegacyAppShell
      brand="S"
      surface="Seller"
      homePath="/seller"
      items={RAIL_ITEMS}
      activeId={activeSection}
      tabs={TABS[activeSection]}
      identity={identity}
      tools={(
        <>
          {!apiMode && <NotificationsPanel />}
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => navigate("/")}>
            View store
          </button>
        </>
      )}
    >
      <Outlet />
    </LegacyAppShell>
  );
}
